/**
 * ② AI 素材整理回显(照 dept-reporter-create.html .mtable):
 * 每行 = 体裁字段 + 取值 + 三色分级 + 来源片段 —— 这张表就是「AI 读懂了什么」,
 * 用户逐行核对来源;缺失字段红虚线 chips(「缺:X(不会编)」),模糊 ≠ 缺失。
 */
import { useState } from "react";

import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";

import { MATERIAL_STATUS_META, fieldLabel, humanizeReporterText } from "../model/labels";
import type { MaterialExtract } from "../model/parse";

export interface SupplyEntry {
  field: string;
  label: string;
  value: string;
}

interface Props {
  material: MaterialExtract;
  /**
   * 就地补齐缺失字段。给了才渲染补充入口(预览/只读处不给)。
   * 拿到的是「用户手填的那几条」,由调用方决定怎么并回素材 —— 表格不碰素材文本。
   */
  onSupply?: (entries: SupplyEntry[]) => void;
  /** 整理在途:补充区禁手,避免连点发两轮。 */
  supplying?: boolean;
}

/**
 * 缺失字段就地补充(2026-08-14 四天反馈校对 TOP#4)。
 *
 * 琳琳 0811:「整理出来的素材,缺失的内容标红,没办法针对性补充,只能补充后重新整理再生成文章」。
 * 此前这排红 chip 是**纯展示**:告诉你缺什么,却不给填的地方 —— 用户只能滚回上面那个大
 * textarea 自己找位置补,再整份重跑。缺的往往是「时间」「地点」这种一句话的事,代价却是
 * 重描述一遍整份素材。
 *
 * ★这一版是**止血版,不是终态**:补完仍走「整份重新整理」(既有 MATERIAL_EXTRACT 那条路),
 *   没有单字段重算接口 —— 那要后端配合,不在这一刀里。所以按钮上写明「重新整理」,
 *   不许让用户以为只补这一条:文案许诺什么,链路就得真做到什么(同族教训:
 *   「按人物选」形同虚设、「本次没有扣积分」位置错)。
 *
 * 填进去的内容会**追加进原始素材**并显示在上面那个输入框里 —— 用户看得见自己补了什么,
 * 下一次重新整理也带着走;不做只在内存里活一次的隐形补丁。
 */
export function MaterialTable({ material, onSupply, supplying = false }: Props) {
  const [openFields, setOpenFields] = useState<string[]>([]);
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const filled = openFields
    .map((f) => ({ field: f, label: fieldLabel(f), value: (drafts[f] ?? "").trim() }))
    .filter((e) => e.value !== "");

  const toggle = (field: string) =>
    setOpenFields((p) => (p.includes(field) ? p.filter((x) => x !== field) : [...p, field]));

  return (
    <div data-reporter-material="">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr className="border-b border-border text-left text-2xs font-semibold text-muted-foreground">
              <th className="px-2 py-1.5">体裁字段</th>
              <th className="px-2 py-1.5">取值</th>
              <th className="px-2 py-1.5">分级</th>
              <th className="px-2 py-1.5">来源</th>
            </tr>
          </thead>
          <tbody>
            {material.fields.map((f) => {
              const meta = MATERIAL_STATUS_META[f.status];
              return (
                <tr key={f.key} className="border-b border-border/60 align-top last:border-0">
                  <td className="whitespace-nowrap px-2 py-2 font-semibold">{fieldLabel(f.key)}</td>
                  <td className="px-2 py-2 leading-relaxed">{humanizeReporterText(f.value)}</td>
                  <td className="whitespace-nowrap px-2 py-2">
                    <span
                      className={`rounded-full px-2 py-0.5 text-2xs font-bold ${meta.className}`}
                    >
                      {meta.label}
                      {f.status !== "explicit" && f.confidence === "low" ? " · 低" : ""}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-2xs leading-relaxed text-muted-foreground">
                    {humanizeReporterText(f.source)}
                  </td>
                </tr>
              );
            })}
            {material.fields.length === 0 && (
              <tr>
                <td colSpan={4} className="px-2 py-4 text-center text-muted-foreground">
                  没有提取到可用字段 —— 素材太短或与体裁不匹配,补充后重新整理。
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {material.missingFields.length > 0 && (
        <div className="mt-2.5 space-y-2" data-reporter-missing="">
          <div className="flex flex-wrap items-center gap-1.5">
            {material.missingFields.map((m) => {
              const on = openFields.includes(m.field);
              const label = `缺:${fieldLabel(m.field)}(不会编)`;
              return onSupply ? (
                <button
                  key={m.field}
                  type="button"
                  aria-pressed={on}
                  aria-label={`补充${fieldLabel(m.field)}`}
                  title={humanizeReporterText(m.suggestion || m.reason)}
                  onClick={() => toggle(m.field)}
                  data-missing-chip={m.field}
                  className={`rounded-full border border-dashed px-2.5 py-0.5 text-2xs transition-colors ${
                    on
                      ? "border-destructive bg-destructive/10 text-destructive"
                      : "border-destructive/50 text-destructive hover:bg-destructive/5"
                  }`}
                >
                  {label} · {on ? "收起" : "点我补"}
                </button>
              ) : (
                <span
                  key={m.field}
                  title={humanizeReporterText(m.suggestion || m.reason)}
                  className="rounded-full border border-dashed border-destructive/50 px-2.5 py-0.5 text-2xs text-destructive"
                >
                  {label}
                </span>
              );
            })}
          </div>

          {onSupply && openFields.length > 0 && (
            <div className="space-y-2 rounded-lg border border-dashed border-destructive/40 bg-destructive/[0.03] p-2.5">
              {openFields.map((f) => {
                const m = material.missingFields.find((x) => x.field === f);
                return (
                  <div key={f} className="space-y-1">
                    <label
                      htmlFor={`supply-${f}`}
                      className="block text-2xs font-semibold text-foreground"
                    >
                      {fieldLabel(f)}
                    </label>
                    <Input
                      id={`supply-${f}`}
                      value={drafts[f] ?? ""}
                      disabled={supplying}
                      placeholder={
                        humanizeReporterText(m?.suggestion || m?.reason) || `补一句${fieldLabel(f)}`
                      }
                      onChange={(e) => setDrafts((p) => ({ ...p, [f]: e.target.value }))}
                      className="h-8 text-xs"
                    />
                  </div>
                );
              })}
              <div className="flex items-center gap-2 pt-0.5">
                <Button
                  size="sm"
                  disabled={supplying || filled.length === 0}
                  data-reporter-supply-submit=""
                  onClick={() => {
                    onSupply(filled);
                    setOpenFields([]);
                    setDrafts({});
                  }}
                >
                  {supplying ? "整理中…" : `补进素材,重新整理(${filled.length})`}
                </Button>
                {/* ★写明代价:补充仍是整份重跑一次整理(约 3~6 积分,见 REPORTER_CREDITS_EXTRACT_RANGE),不是只算这一条。
                    页面上不说,用户会以为点一下只补这一格 —— 那就成了「许诺了没做到的事」。 */}
                <span className="text-2xs text-muted-foreground">
                  会把填的内容追加进上面的原始素材,再整份重新整理一次
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {material.uncertainFields.length > 0 && (
        <p className="mt-2 text-2xs leading-relaxed text-muted-foreground">
          模糊 ≠ 缺失:
          {material.uncertainFields
            .map((u) => `「${u.text || u.field}」${u.risk ? `(${u.risk})` : ""}`)
            .join("、")}
          进字段但标不确定;时间 / 地点 / 价格这类字段永远不做推断。
        </p>
      )}
    </div>
  );
}
